import React, { useState } from 'react';
import styled from 'styled-components';
import Rodal from 'rodal';
import { I18n } from 'react-redux-i18n';

import { ReactComponent as HelpCircleIcon } from '../../assets/icons/help-circle.svg';
import { ReactComponent as CloseIconSvg } from '../../assets/icons/close.svg';

import SupportTicketForm from './SupportTicketForm';
import SupportTicketSuccess from './SupportTicketSuccess';

const SupportWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formInstanceKey, setFormInstanceKey] = useState(0);

  const openWidget = () => {
    setIsOpen(true);
    setIsSubmitted(false);
    // Remount the form fresh each time the widget is opened, so a previous attempt's
    // error banner, attached files, or field values don't linger from a prior session.
    setFormInstanceKey((prev) => prev + 1);
  };
  const closeWidget = () => setIsOpen(false);

  return (
    <>
      <LauncherButton onClick={openWidget}>
        <HelpCircleIcon />
        {I18n.t('I need help')}
      </LauncherButton>
      <Rodal
        visible={isOpen}
        onClose={closeWidget}
        animation="fade"
        showCloseButton={false}
        customStyles={{
          width: 'auto',
          height: 'auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'transparent',
          overflow: 'visible',
        }}
      >
        <ModalContainer>
          <Banner>
            <BannerCloseButton onClick={closeWidget}>
              <CloseIconSvg />
            </BannerCloseButton>
            <BannerTitle>{I18n.t('Support / Feedback')}</BannerTitle>
          </Banner>
          <Content>
            {isSubmitted ? (
              <SupportTicketSuccess onClose={closeWidget} />
            ) : (
              <SupportTicketForm key={formInstanceKey} onSubmitted={() => setIsSubmitted(true)} />
            )}
          </Content>
        </ModalContainer>
      </Rodal>
    </>
  );
};

const LauncherButton = styled.button`
  position: fixed;
  right: 2.4rem;
  bottom: 2.4rem;
  z-index: 999;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  border: none;
  outline: none;
  padding: 1.2rem 2rem;
  border-radius: 6rem;
  background-color: #2b6663;
  color: ${(props) => props.theme.colors.white};
  font-size: ${(props) => props.theme.fontSizes.default};
  font-weight: bold;
  box-shadow: ${(props) => props.theme.shadows.default};
  transition: 0.2s all;

  svg {
    width: 1.8rem;
    height: 1.8rem;
  }

  :hover {
    cursor: pointer;
    opacity: 0.9;
  }
`;

const ModalContainer = styled.div`
  width: 40rem;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  border-radius: 1.2rem;
  overflow: hidden;
  box-shadow: 0 3px 32px rgba(0, 0, 0, 0.14);
`;

const Banner = styled.div`
  position: relative;
  min-height: 8rem;
  padding: 2rem 2.4rem;
  display: flex;
  align-items: flex-end;
  background: linear-gradient(
    135deg,
    ${(props) => props.theme.colors.darkBlue2},
    ${(props) => props.theme.colors.mediumBlue}
  );
`;

const BannerTitle = styled.h3`
  font-size: ${(props) => props.theme.fontSizes.large};
  font-weight: bold;
  color: ${(props) => props.theme.colors.white};
`;

const BannerCloseButton = styled.button`
  position: absolute;
  top: 1.2rem;
  right: 1.2rem;
  width: 3.2rem;
  height: 3.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  outline: none;
  border-radius: 0.6rem;
  background-color: rgba(0, 0, 0, 0.2);

  svg {
    width: 1.2rem;
    height: 1.2rem;
  }

  :hover {
    cursor: pointer;
    background-color: rgba(0, 0, 0, 0.32);
  }
`;

const Content = styled.div`
  flex-grow: 1;
  overflow-y: auto;
  padding: 2.4rem;
  background-color: ${(props) => props.theme.colors.white};
`;

export default SupportWidget;
